import z from "zod";
import EmailTemplatesService from "../../../../services/EmailTemplatesService";
import { mapErrorsToFields } from "../../../../utils";

const api = new EmailTemplatesService();
export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    description?: string[];
    subject?: string[];
    body?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    name?: string;
    description?: string;
    subject?: string;
    body?: string;
  };
};

const FormSchema = z.object({
  name: z.string().min(1, { message: "name is required." }).refine((s) => !s.includes(" "), "Spaces not allowed!"),
  description: z.string().optional(),
  subject: z.string().min(1, { message: "subject is required." }),
  body: z.string().min(1, { message: "body is required." }),
});

export async function createTemplate(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
    description: formData.get("description")?.toString() || "",
    subject: formData.get("subject")?.toString() || "",
    body: formData.get("body")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update template.",
        defaultValues,
        success: false,
      };
    }
    const { name, description, subject, body } = validatedFields.data;
    const result = await api.createEmailTemplate({
      name,
      description,
      subject,
      body,
    });
    if (result.status === 201)
      return {
        errors: {},
        message: "Template Created successfully.",
        defaultValues,
        success: true,
      };
    else if (result.status === 400)
      return {
        errors: {},
        message: result.data.description,
        defaultValues,
        success: false,
      };
  } catch (error) {
    console.error("Error updating template:", error);
  }
  return {
    errors: {},
    message: "Failed to update template.",
    defaultValues,
    success: false,
  };
}

export async function updateTemplate(
  id: string,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
    description: formData.get("description")?.toString() || "",
    subject: formData.get("subject")?.toString() || "",
    body: formData.get("body")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update template.",
        defaultValues,
        success: false,
      };
    }
    const { name, description, subject, body } = validatedFields.data;
    const result = await api.updateEmailTemplate(id, {
      name,
      description,
      subject,
      body,
    });
    if (result.status === 200)
      return {
        errors: {},
        message: "Template updated successfully.",
        defaultValues,
        success: true,
      };
  } catch (error) {
    console.error("Error updating template:", error);
  }
  return {
    errors: {},
    message: "Failed to update template.",
    defaultValues,
    success: false,
  };
}

export async function deleteTemplate(
  id: string
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteEmailTemplate(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Database Error:", error);
  }
  return { success: false };
}
